/* ============================================================
   Cathaya × Minimog — Cart Drawer + Ajax ATC
   - Defines window.openCartDrawer / closeCartDrawer (Cathaya
     cart-drawer.liquid uses inline onclick="closeCartDrawer()")
   - Intercepts every POST to /cart/add (forms AND fetch),
     refreshes drawer via Section Rendering API, refreshes
     header cart count via /cart.js, then opens drawer.
   - Wires drawer qty +/- and remove buttons (data-line based).
   - Coexists with Minimog's vendor.js / theme-global.js — we
     simply replace the drawer DOM after each cart mutation, so
     whatever Minimog dispatched is overwritten by fresh server
     markup.
   ============================================================ */

(function () {
  "use strict";

  var DRAWER_ID = "cart-drawer";
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  // ---------- Open / close (exposed as globals) ----------

  function openCartDrawer() {
    var d = document.getElementById(DRAWER_ID);
    if (!d) return;
    d.classList.add("is-open");
    d.setAttribute("aria-hidden", "false");
    d.setAttribute("aria-modal", "true");
    document.documentElement.style.overflow = "hidden";
  }

  function closeCartDrawer() {
    var d = document.getElementById(DRAWER_ID);
    if (!d) return;
    d.classList.remove("is-open");
    d.setAttribute("aria-hidden", "true");
    d.removeAttribute("aria-modal");
    document.documentElement.style.overflow = "";
  }

  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  // ---------- Refresh ----------

  function refreshDrawerHTML() {
    return fetch(ROOT + "?sections=" + DRAWER_ID, { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[DRAWER_ID];
        if (!html) return;
        var wrap = document.createElement("div");
        wrap.innerHTML = html.trim();
        // Section response wraps in <div id="shopify-section-...">; find our drawer inside.
        var fresh = wrap.querySelector("#" + DRAWER_ID);
        var current = document.getElementById(DRAWER_ID);
        if (fresh && current) {
          var wasOpen = current.classList.contains("is-open");
          current.replaceWith(fresh);
          if (wasOpen) openCartDrawer();
        }
      })
      .catch(function (e) { console.warn("[cathaya-cart] drawer refresh failed", e); });
  }

  function refreshCartCount() {
    return fetch(ROOT + "cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll(".c-cart-count").forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll(".c-cart-link").forEach(function (el) {
          el.setAttribute("aria-label", "Cart, " + cart.item_count + " items");
        });
      })
      .catch(function (e) { console.warn("[cathaya-cart] count refresh failed", e); });
  }

  function refreshAll() {
    return Promise.all([refreshDrawerHTML(), refreshCartCount()]);
  }

  // Coalesce refresh requests. Rapid /cart/change calls (e.g. user
  // spamming +/-) should only result in ONE drawer DOM replacement
  // (the last one wins). Returns a single shared promise per tick.
  var pendingRefresh = null;
  function refreshAllCoalesced() {
    if (pendingRefresh) return pendingRefresh;
    pendingRefresh = new Promise(function (resolve) {
      requestAnimationFrame(function () {
        refreshAll().then(function () {
          pendingRefresh = null;
          resolve();
        }, function () {
          pendingRefresh = null;
          resolve();
        });
      });
    });
    return pendingRefresh;
  }

  // ---------- ATC interception ----------

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.dataset.cathayaOriginal = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add("cathaya-is-loading");
    } else {
      btn.disabled = false;
      btn.classList.remove("cathaya-is-loading");
      if (btn.dataset.cathayaOriginal) {
        btn.innerHTML = btn.dataset.cathayaOriginal;
        delete btn.dataset.cathayaOriginal;
      }
    }
  }

  function onSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute("action") || "";
    if (!/\/cart\/add/.test(action)) return;
    // If Minimog's <product-form> (or any custom element ending in -form) wraps
    // this form, let its own handler run — the fetch monkey-patch below will
    // still catch the resulting /cart/add.js call and open our drawer.
    if (form.closest("product-form, quick-add-form, m-product-form, [data-product-form]")) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var btn = form.querySelector('[type="submit"], [name="add"]');
    setButtonLoading(btn, true);
    var fd = new FormData(form);

    fetch(ROOT + "cart/add.js", {
      method: "POST",
      body: fd,
      credentials: "same-origin",
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      // refreshAll + openCartDrawer run from the fetch monkey-patch below,
      // which fires for any successful POST to /cart/add.js. We only need
      // to handle the button state and surface errors here.
      .catch(function (err) {
        console.error("[cathaya-cart] ATC failed", err);
        var msg = (err && err.description) || "Could not add to cart.";
        alert(msg);
      })
      .finally(function () { setButtonLoading(btn, false); });
  }

  // Capture phase so we beat any other handlers (Minimog included).
  document.addEventListener("submit", onSubmit, true);

  // ---------- Drawer qty + remove ----------

  function changeLine(line, qty) {
    // refreshAll runs from the fetch monkey-patch on success.
    return fetch(ROOT + "cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ line: line, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  document.addEventListener("click", function (e) {
    // Remove
    var rm = e.target.closest(".c-drawer-item-remove");
    if (rm) {
      e.preventDefault();
      var line = parseInt(rm.dataset.line, 10);
      if (line) changeLine(line, 0);
      return;
    }
    // Qty +/-
    var qb = e.target.closest(".c-drawer-qty-btn");
    if (qb) {
      e.preventDefault();
      var lineEl = qb.closest(".c-drawer-item");
      var valEl = lineEl && lineEl.querySelector(".c-drawer-qty-value");
      if (!lineEl || !valEl) return;
      var cur = parseInt(valEl.textContent, 10) || 0;
      var next = qb.dataset.action === "plus" ? cur + 1 : cur - 1;
      if (next < 0) next = 0;
      var line2 = parseInt(qb.dataset.line, 10);
      if (line2) changeLine(line2, next);
    }
  });

  // ---------- Open drawer from header cart icons ----------
  // Cathaya's cart icon is a plain link to /cart. We hijack the click
  // so it opens the drawer instead (matching common Shopify UX). The
  // /cart page still works via direct URL or "View Full Cart" button.

  document.addEventListener("click", function (e) {
    var link = e.target.closest(".c-cart-link");
    if (!link) return;
    // Allow modifier-clicks to behave normally (open in new tab, etc.)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    openCartDrawer();
  });

  // ---------- Esc to close ----------

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closeCartDrawer();
  });

  // ---------- window.fetch monkey-patch (catches Minimog product-form,
  //            Quick Add, Buy Buttons, anything that calls /cart/add.js
  //            without going through a <form> submit event) ----------

  if (window.fetch && !window.__cathayaCartFetchPatched) {
    window.__cathayaCartFetchPatched = true;
    var origFetch = window.fetch.bind(window);

    function isCartMutation(input, init) {
      var url = "";
      if (typeof input === "string") url = input;
      else if (input instanceof Request) url = input.url;
      else if (input && input.url) url = input.url;
      if (!url) return null;
      if (/\/cart\/add(\.js)?(\?|$)/.test(url))    return "add";
      if (/\/cart\/change(\.js)?(\?|$)/.test(url)) return "change";
      if (/\/cart\/update(\.js)?(\?|$)/.test(url)) return "update";
      if (/\/cart\/clear(\.js)?(\?|$)/.test(url))  return "clear";
      return null;
    }

    window.fetch = function (input, init) {
      var op = isCartMutation(input, init);
      var method = (init && init.method) || (input instanceof Request && input.method) || "GET";
      var isMutating = op && /POST|PUT|PATCH|DELETE/i.test(method);
      // Skip the section-rendering requests we make ourselves (GET only).
      if (!isMutating) return origFetch(input, init);

      return origFetch(input, init).then(function (res) {
        // Only react on success. Defer so the original caller's .then
        // runs first (UI updates, toast, analytics) before we replace
        // the drawer DOM. Coalesce so rapid mutations only refresh once.
        if (res && res.ok) {
          setTimeout(function () {
            refreshAllCoalesced().then(function () {
              if (op === "add") openCartDrawer();
            });
          }, 0);
        }
        return res;
      });
    };
  }
})();
